/**
 * Phone numbers, punctuated as they are typed.
 *
 * A rep enters a number ten digits at a time and then has to read it back to
 * somebody, usually off a phone screen in a driveway. Unpunctuated, 2515550147
 * is a string nobody can check against the one they were given.
 *
 * So the dashes go in while the number is being typed rather than on save:
 * formatting on blur moves the caret and re-renders what somebody is still
 * looking at. Everything here is about a partly-typed number being left alone
 * until there is enough of it to punctuate.
 */

/** Every digit in the string, in order. */
const digitsOf = (value: string): string => value.replace(/\D/g, "");

/**
 * A US number as it should be typed, from whatever has been typed so far.
 *
 * Rules, in the order they matter:
 *
 * - Fewer than four digits is left bare. "251" is not "251-", and appending a
 *   dash the moment the third digit lands means backspace deletes the dash
 *   rather than the digit and the field cannot be emptied.
 * - Ten digits is 251-555-0147.
 * - A leading 1 on eleven digits is the country code: 1-251-555-0147.
 * - Anything else — an extension, an international number, a number half
 *   pasted — is handed back exactly as it came. This function's job is to make
 *   the ordinary case readable, not to decide what a phone number is allowed
 *   to be. Refusing to show what somebody typed loses data.
 */
export function formatPhoneInput(value: string): string {
  const digits = digitsOf(value);

  // Nothing typed, or only punctuation: give back the empty string so the box
  // clears rather than keeping a stray bracket.
  if (digits === "") return "";

  // A number that is longer than a US one with a country code is something
  // else — an extension, or somewhere that is not here.
  if (digits.length > 11) return value;

  if (digits.length === 11) {
    if (!digits.startsWith("1")) return value;
    return `1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  if (digits.length > 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length > 3) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return digits;
}

/**
 * A stored number, punctuated for reading.
 *
 * Same rules, but null in and null out — the contact rows written before this
 * existed hold bare digits, and they should read the same as the ones typed
 * today.
 */
export function formatPhone(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return formatPhoneInput(trimmed) || trimmed;
}

/**
 * The number to ring, out of the two a contact has.
 *
 * The cell leads. It is the customer's own phone, it is the one answered in a
 * driveway, and the other number is as likely to be their spouse's as their
 * own. Every screen that has room for one number uses this, so a list and the
 * page it opens cannot end up showing different numbers for the same person.
 */
export function primaryPhone(
  contact: { cell?: string | null; phone?: string | null } | null | undefined,
): string | null {
  if (!contact) return null;
  return contact.cell?.trim() || contact.phone?.trim() || null;
}

/**
 * What to call the second number.
 *
 * "Alternate phone" until somebody says whose it is. A rep looking at two bare
 * numbers has no way to tell which one puts them through to the person who
 * signs, and the answer — "that's his wife's" — only ever lived in someone's
 * head. Written down, it belongs beside the number.
 */
export function altPhoneLabel(
  contact: { phone_label?: string | null } | null | undefined,
): string {
  return contact?.phone_label?.trim() || "Alternate phone";
}

/**
 * True when there is either nothing here or a whole US number.
 *
 * Used to warn, never to block: a rep with seven digits of a number is better
 * off saving it than losing it.
 */
export function isCompletePhone(value: string | null | undefined): boolean {
  const digits = digitsOf(value ?? "");
  if (digits.length === 0) return true;
  if (digits.length === 10) return true;
  return digits.length === 11 && digits.startsWith("1");
}
